import React, { FC, useCallback, useEffect, useRef, useState } from "react"
import { IAudio } from "./audio-types"
import PlaySvg from 'src/images/icons/play.inline.svg'
import PauseSvg from 'src/images/icons/pause.inline.svg'
import VolumeUpSvg from 'src/images/icons/volume-up.inline.svg'
import VolumeOffSvg from 'src/images/icons/volume-off.inline.svg'
import AudioPlayerSlider from "./AudioPlayerSlider"
import tailwindConfig from 'src/../tailwind.config'
import resolveConfig from 'tailwindcss/resolveConfig'
import { CustomColorConfig } from "../calendar/calendar-color-mapping"
import AudioProgress from "./AudioProgress"
import create from "zustand"
import LoaderSpinner from "../ui/LoaderSpinner"

interface IAudioPlayerProps {
  content?: IAudio | null
  className?: string | null
}

interface IAudioPlayerStore {
  stopCurrentPlayback?: () => void
  setStopCurrentPlayback: (action?: () => void) => void
}

const colors = resolveConfig(tailwindConfig).theme.colors as CustomColorConfig

const useAudioPlayerStore = create<IAudioPlayerStore>((set) => ({
  stopCurrentPlayback: null,
  setStopCurrentPlayback: (action) => set(() => ({ stopCurrentPlayback: action })),
}))

const AudioPlayer: FC<IAudioPlayerProps> = ({ content, className }) => {
  const player = useRef<HTMLAudioElement>(null)
  const loadAudioRetryCount = useRef(2)
  const requestedAnimationFrame = useRef(null)
  const isPlaying = useRef(false)
  const [playing, setPlaying] = useState(false)
  const [muted, setMuted] = useState(false)
  const [volume, setVolume] = useState(50)
  const [progress, setProgress] = useState(0)
  const [loading, setLoading] = useState(false)
  const setStopCurrentPlayback = useAudioPlayerStore((state) => state.setStopCurrentPlayback)
  const stopCurrentPlayback = useRef(useAudioPlayerStore.getState().stopCurrentPlayback)

  const stopUpdatingControls = () => {
    if (requestedAnimationFrame.current) {
      cancelAnimationFrame(requestedAnimationFrame.current)
      requestedAnimationFrame.current = null
    }
  }
  
  const stopPlaying = () => {
    stopUpdatingControls()
    setPlaying(false)
    setLoading(false)
    isPlaying.current = false
    if (player.current) {
      player.current.pause()
    }
  }

  useEffect(() => {
    const unsubscribe = useAudioPlayerStore.subscribe((state) => {
      stopCurrentPlayback.current = state.stopCurrentPlayback
    })
    return () => {
      unsubscribe()
      stopPlaying()
      if (isPlaying.current) {
        setStopCurrentPlayback(undefined)
      }
      isPlaying.current = false
    }
  }, [])

  const initializePlayer = useCallback<(node: HTMLAudioElement) => void>((node) => {
    player.current = node
    if (node) {
      node.volume = volume / 100
    }
  }, [])
  
  if (!content) {
    return <></>
  }

  const onAudioError = () => {
    if (loadAudioRetryCount.current > 0) {
      player.current.load()
    }
    loadAudioRetryCount.current--
  }

  const startUpdatingControls = () => {
    if (player.current) {
      setProgress(player.current.currentTime)
    }
    const animationFrame = requestAnimationFrame(startUpdatingControls)
    requestedAnimationFrame.current = animationFrame
  }

  const startPlaying = () => {
    const startPlayingPromise = player.current.play()
    if (startPlayingPromise) {
      setLoading(true)
      startPlayingPromise
        .catch(() => {})
        .finally(() => { 
          if (isPlaying.current) {
            setLoading(false)
          } 
        })
    }
    startUpdatingControls()
    setPlaying(true)
    isPlaying.current = true
  }

  const togglePlaying = () => {
    if (playing) {
      if (!player.current.paused) {
        stopPlaying()
        setStopCurrentPlayback(undefined)
      }
    } else {
      if (player.current.paused) {
        stopCurrentPlayback.current?.()
        setStopCurrentPlayback(stopPlaying)
        startPlaying()
      }
    }
  }

  const toggleMuted = () => {
    const newMuted = !muted
    setMuted(newMuted)
    player.current.muted = newMuted
  }

  const blockBubbling = (event: React.MouseEvent | React.KeyboardEvent) => {
    event.stopPropagation()
  }

  const onProgressUpdateStart = (value: number) => {
    stopUpdatingControls()
    setProgress(value)
  }
  
  const onProgressUpdateEnd = (value: number) => {
    setProgress(value)
    player.current.currentTime = value
    if (playing) {
      startUpdatingControls()
    }
  }

  const onVolumeChange = (newValue: number) => {
    setVolume(newValue)
    if (newValue > 0) {
      setMuted(false)
      player.current.muted = false
    }
    player.current.volume = newValue / 100
  }

  const onPlaybackEnded = () => {
    stopUpdatingControls()
    setPlaying(false)
    setProgress(0)
  }
  
  return (
    <div 
      className={`relative flex items-center text-sm${className ? ` ${className}` : ''}`}
      onClick={blockBubbling}>
      <audio 
        ref={initializePlayer} 
        onError={onAudioError} 
        onEnded={onPlaybackEnded}
        preload="none">
        <source src={content.url} type={content.mimeType} />
      </audio>
      <button 
        type="button" 
        className="p-0.5 flex items-center mr-1 rounded-full transition-colors ease-in-out duration-200 hover:bg-slate-200"
        onClick={togglePlaying}>
        { playing
          ? <PauseSvg className="w-5 h-5" fill={colors.deepDarkBlue}/>
          : <PlaySvg className="w-5 h-5" fill={colors.deepDarkBlue}/>
        }
      </button>
      <AudioProgress
        duration={content.duration}
        progress={progress}
        onInput={onProgressUpdateStart}
        onChange={onProgressUpdateEnd}
        className="mr-2 w-full"
      />
      <div className="shrink-0 transition-all ease-in-out duration-300 h-6 w-7 hover:w-20 overflow-hidden py-0.5 px-2 flex items-center justify-end rounded-full hover:bg-slate-200">
        <div className="w-full mr-1 overflow-hidden flex items-center">
          <AudioPlayerSlider 
            max={100}
            value={volume}
            step={1}
            passedColor={colors.grayBlue}
            remainingColor={colors.navyBlueGray} 
            className="w-full"  
            onUpdateStart={onVolumeChange}
            disabled={false}
          />
        </div>
        <button type="button" onClick={toggleMuted}>
          { muted || volume === 0
            ? <VolumeOffSvg className="w-5 h-5" fill={colors.deepDarkBlue} />
            : <VolumeUpSvg className="w-5 h-5" fill={colors.deepDarkBlue} />
          }
        </button>
      </div>
      { loading &&
        <div className="absolute left-[45%] scale-[.5] pointer-events-none">
          <LoaderSpinner />
        </div>
      }
    </div>
  )
}

export default AudioPlayer